/**
 * Plan2 / Treatment2 status vocabulary and helpers (Exam2-style table mode).
 */

export const PLAN2_NOT_SELECTED = "not selected";
export const PLAN2_ORDERED_PENDING = "Ordered, pending";
/** Default for Medications / Therapies / Treatments *2 (no pending-flip). */
export const PLAN2_ORDERED = "Ordered";
export const PLAN2_NORMAL_REASSURING = "Normal / Reassuring";

/** Diagnostic *2 status chips (lab / imaging / procedures / diagnostics). */
export const PLAN2_STATUS_OPTIONS = [
  PLAN2_NOT_SELECTED,
  PLAN2_ORDERED_PENDING,
  PLAN2_NORMAL_REASSURING,
];

/** Treatment *2 status chips (medications / therapies / treatments). */
export const TREATMENT2_STATUS_OPTIONS = [
  PLAN2_NOT_SELECTED,
  PLAN2_ORDERED,
  PLAN2_NORMAL_REASSURING,
];

/** @typedef {"diagnostic" | "treatment"} Plan2Variant */

/** Diagnostic test categories that get their own Laboratory|Laboratory 2 (etc.) toggles. */
export const PLAN2_TEST_CATEGORY_KEYS = ["lab", "imaging", "procedure", "other"];

export const PLAN2_TEST_TOGGLE_LABELS = {
  lab: { list: "Laboratory", table: "Laboratory 2" },
  imaging: { list: "Imaging", table: "Imaging 2" },
  procedure: { list: "Procedures", table: "Procedures 2" },
  other: { list: "Diagnostics", table: "Diagnostics 2" },
};

/**
 * Treatment UI groups for *2 mode.
 * medication / therapy are standalone; remaining categories share Treatments 2.
 */
export const TREATMENT2_GROUP_KEYS = ["medication", "therapy", "treatmentsBundle"];

export const TREATMENT2_TOGGLE_LABELS = {
  medication: { list: "Medications", table: "Medications 2" },
  therapy: { list: "Therapies", table: "Therapies 2" },
  treatmentsBundle: { list: "Treatments", table: "Treatments 2" },
};

export const TREATMENT2_BUNDLE_CATEGORIES = ["procedure", "lifestyle", "referral", "other"];

export function treatment2GroupKey(category) {
  if (category === "medication") return "medication";
  if (category === "therapy") return "therapy";
  return "treatmentsBundle";
}

const uniqTrimmed = (items) => [
  ...new Set(
    (Array.isArray(items) ? items : [])
      .map((item) => String(item || "").trim())
      .filter(Boolean),
  ),
];

export function normalizePlan2Values(value) {
  if (Array.isArray(value)) return uniqTrimmed(value);
  const text = String(value || "").trim();
  if (!text) return [];
  if (text.includes(";")) return uniqTrimmed(text.split(/\s*;\s*/));
  return [text];
}

const isNotSelectedStatus = (value) =>
  String(value || "").trim().toLowerCase() === PLAN2_NOT_SELECTED.toLowerCase();

const isOrderedLikeStatus = (value) => {
  const text = String(value || "").trim().toLowerCase();
  return (
    text === PLAN2_ORDERED_PENDING.toLowerCase() ||
    text === PLAN2_ORDERED.toLowerCase()
  );
};

/**
 * Autocomplete selection rules for *2 rows:
 * - Choosing "not selected" replaces the row (clears Ordered / Ordered, pending / anything else).
 * - Choosing any other status drops "not selected".
 */
export function reconcilePlan2Selection(nextValues, previousValues = []) {
  const next = normalizePlan2Values(nextValues);
  const previous = normalizePlan2Values(previousValues);
  const hadNotSelected = previous.some(isNotSelectedStatus);
  const hasNotSelected = next.some(isNotSelectedStatus);

  if (hasNotSelected && !hadNotSelected) {
    return [PLAN2_NOT_SELECTED];
  }
  if (hasNotSelected && next.length > 1) {
    // User added another chip while "not selected" was present — keep the new work, drop exclusion.
    return next.filter((item) => !isNotSelectedStatus(item));
  }
  if (hasNotSelected) {
    return [PLAN2_NOT_SELECTED];
  }
  // Defense: never keep Ordered* alongside not selected (already handled above).
  return next.filter((item) => !(hadNotSelected && isOrderedLikeStatus(item)));
}

export function formatPlan2ValueText(value) {
  return normalizePlan2Values(value).join("; ");
}

/** True when the row should be omitted from documentation. */
export function isPlan2Excluded(value) {
  const values = normalizePlan2Values(value);
  if (!values.length) return true;
  return values.every((item) => item.toLowerCase() === PLAN2_NOT_SELECTED.toLowerCase());
}

/** Statuses that count as "normal" (non-red) for chip coloring. */
export function isPlan2NormalStatus(value) {
  const text = String(value || "").trim().toLowerCase();
  if (!text) return false;
  return (
    text === PLAN2_ORDERED_PENDING.toLowerCase() ||
    text === PLAN2_ORDERED.toLowerCase() ||
    text === PLAN2_NORMAL_REASSURING.toLowerCase() ||
    text === "normal / reassuring" ||
    text === "normal" ||
    text === "reassuring" ||
    text === PLAN2_NOT_SELECTED.toLowerCase()
  );
}

/**
 * Preferred Tab / Document-normal fill for a row.
 * Diagnostic: Ordered, pending → normal/reassuring after pendingFlip.
 * Treatment: always Ordered (no pending-flip).
 */
export function getPreferredPlan2Status(
  itemId,
  { pendingFlip = {}, variant = "diagnostic" } = {},
) {
  if (variant === "treatment") return PLAN2_ORDERED;
  if (pendingFlip?.[itemId]) return PLAN2_NORMAL_REASSURING;
  return PLAN2_ORDERED_PENDING;
}

/**
 * @param {"table"|"list"} [mode="table"] Events lean *2 (table); Admission/Progress/outpatient
 *   default to legacy list until the clinician toggles *2.
 */
export function emptyPlan2CategoryState(mode = "table") {
  return {
    mode: mode === "list" ? "list" : "table",
    values: {},
    pendingFlip: {},
  };
}

export function createEmptyPlan2ByCategory(mode = "table") {
  return Object.fromEntries(
    PLAN2_TEST_CATEGORY_KEYS.map((key) => [key, emptyPlan2CategoryState(mode)]),
  );
}

export function createEmptyTreatment2ByCategory(mode = "table") {
  return Object.fromEntries(
    TREATMENT2_GROUP_KEYS.map((key) => [key, emptyPlan2CategoryState(mode)]),
  );
}

function normalizePendingFlipMap(raw) {
  const next = {};
  if (!raw || typeof raw !== "object") return next;
  Object.entries(raw).forEach(([id, flagged]) => {
    if (flagged) next[id] = true;
  });
  return next;
}

function normalizeCategoryState(raw) {
  const base = emptyPlan2CategoryState();
  if (!raw || typeof raw !== "object") return base;
  const values = {};
  Object.entries(raw.values || {}).forEach(([id, value]) => {
    values[id] = normalizePlan2Values(value);
  });
  return {
    mode: raw.mode === "table" ? "table" : "list",
    values,
    pendingFlip: normalizePendingFlipMap(raw.pendingFlip),
  };
}

/** Hydrate / sanitize persisted plan2ByCategory. */
export function normalizePlan2ByCategory(raw) {
  const next = createEmptyPlan2ByCategory();
  PLAN2_TEST_CATEGORY_KEYS.forEach((key) => {
    next[key] = normalizeCategoryState(raw?.[key]);
  });
  return next;
}

/** Hydrate / sanitize persisted treatment2ByCategory. */
export function normalizeTreatment2ByCategory(raw) {
  const next = createEmptyTreatment2ByCategory();
  TREATMENT2_GROUP_KEYS.forEach((key) => {
    next[key] = normalizeCategoryState(raw?.[key]);
  });
  return next;
}

/**
 * True when Plan2 / Treatment2 state should be persisted.
 * Default empty table mode alone is not dirty; list mode or any values/flips are.
 */
export function hasPlan2State(byCategory) {
  if (!byCategory || typeof byCategory !== "object") return false;
  return Object.values(byCategory).some((state) => {
    if (!state) return false;
    if (state.mode === "list") return true;
    if (state.pendingFlip && Object.keys(state.pendingFlip).length) return true;
    if (state.values && Object.keys(state.values).length) return true;
    return false;
  });
}

/**
 * Ensure every item has an initial value when entering table mode.
 * Diagnostic: Ordered, pending. Treatment: empty (Add another status).
 * Does not overwrite existing values.
 * @param {object} categoryState
 * @param {string[]} itemIds
 * @param {{ variant?: Plan2Variant }} [options]
 */
export function ensurePlan2Defaults(categoryState, itemIds = [], { variant = "diagnostic" } = {}) {
  const state = categoryState || emptyPlan2CategoryState();
  const values = { ...(state.values || {}) };
  const initial =
    variant === "treatment"
      ? []
      : [getPreferredPlan2Status("__default__", { variant: "diagnostic" })];
  let changed = false;
  itemIds.forEach((id) => {
    if (!id) return;
    if (!Object.prototype.hasOwnProperty.call(values, id)) {
      values[id] = initial;
      changed = true;
    }
  });
  if (!changed) return state;
  return { ...state, values };
}

/** Lab / Imaging / Diagnostics bulk action (not Procedures). */
export function usesMarkAllReassuring(categoryKey) {
  return categoryKey === "lab" || categoryKey === "imaging" || categoryKey === "other";
}

export function getPlan2BulkActionLabel(categoryKey) {
  if (usesMarkAllReassuring(categoryKey)) return "Mark All Reassuring";
  if (categoryKey === "procedure") return "Document normal Procedures";
  return "Document normal";
}

export function getTreatment2BulkActionLabel(groupKey) {
  if (groupKey === "medication") return "Mark All Medications as ordered";
  if (groupKey === "therapy") return "Mark All Therapies as ordered";
  if (groupKey === "treatmentsBundle") return "Mark All Treatments as ordered";
  return "Mark All as ordered";
}

/**
 * On blur of a row still showing Ordered, pending → mark pendingFlip for next focus.
 */
export function markPlan2PendingFlip(categoryState, itemId) {
  const state = categoryState || emptyPlan2CategoryState();
  const values = normalizePlan2Values(state.values?.[itemId]);
  const stillPending =
    values.length === 1 &&
    values[0].toLowerCase() === PLAN2_ORDERED_PENDING.toLowerCase();
  if (!stillPending) return state;
  return {
    ...state,
    pendingFlip: { ...(state.pendingFlip || {}), [itemId]: true },
  };
}

/**
 * On focus: if pendingFlip and value is still Ordered, pending → promote to normal/reassuring.
 */
export function applyPlan2FocusFlip(categoryState, itemId) {
  const state = categoryState || emptyPlan2CategoryState();
  if (!state.pendingFlip?.[itemId]) return { state, flipped: false };
  const values = normalizePlan2Values(state.values?.[itemId]);
  const stillPending =
    values.length === 1 &&
    values[0].toLowerCase() === PLAN2_ORDERED_PENDING.toLowerCase();
  if (!stillPending) return { state, flipped: false };
  return {
    state: {
      ...state,
      values: {
        ...(state.values || {}),
        [itemId]: [PLAN2_NORMAL_REASSURING],
      },
    },
    flipped: true,
  };
}

/**
 * Fill all items with preferred status (e.g. Document normal Procedures).
 * @param {object} categoryState
 * @param {string[]} itemIds
 * @param {{ variant?: Plan2Variant }} [options]
 */
export function fillPlan2Normals(categoryState, itemIds = [], { variant = "diagnostic" } = {}) {
  const state = categoryState || emptyPlan2CategoryState();
  const values = { ...(state.values || {}) };
  itemIds.forEach((id) => {
    if (!id) return;
    values[id] = [
      getPreferredPlan2Status(id, { pendingFlip: state.pendingFlip, variant }),
    ];
  });
  return { ...state, values };
}

/**
 * Lab/Imaging/Diagnostics: only rows that are exactly Ordered, pending → Normal / Reassuring.
 */
export function fillPlan2ReassuringFromPending(categoryState, itemIds = []) {
  const state = categoryState || emptyPlan2CategoryState();
  const values = { ...(state.values || {}) };
  let changed = false;
  itemIds.forEach((id) => {
    if (!id) return;
    const current = normalizePlan2Values(values[id]);
    const exactlyPending =
      current.length === 1 &&
      current[0].toLowerCase() === PLAN2_ORDERED_PENDING.toLowerCase();
    if (!exactlyPending) return;
    values[id] = [PLAN2_NORMAL_REASSURING];
    changed = true;
  });
  if (!changed) return state;
  return { ...state, values };
}

/** Medications / Therapies / Treatments: set every row to Ordered. */
export function fillAllPlan2Ordered(categoryState, itemIds = []) {
  const state = categoryState || emptyPlan2CategoryState();
  const values = { ...(state.values || {}) };
  itemIds.forEach((id) => {
    if (!id) return;
    values[id] = [PLAN2_ORDERED];
  });
  return { ...state, values };
}

/**
 * Build Clinical Hints tip lines from item rationales (Exam2 parallel).
 * @param {Array<{id: string, name?: string, rationale?: string, linkedDiagnoses?: string[]}>} items
 */
export function buildPlan2Hints(items = []) {
  return (Array.isArray(items) ? items : [])
    .map((item) => {
      const name = String(item?.name || "").trim();
      const rationale = String(item?.rationale || "").trim();
      if (!name || !rationale) return null;
      const diagnoses = Array.isArray(item.linkedDiagnoses)
        ? item.linkedDiagnoses.map((d) => String(d || "").trim()).filter(Boolean)
        : [];
      return {
        itemId: item.id,
        name,
        rationale,
        diagnoses,
        line: `${name}: ${rationale}${diagnoses.length ? ` (${diagnoses.join(", ")})` : ""}`,
      };
    })
    .filter(Boolean);
}

/**
 * Format documentation lines for table-mode items (excludes not selected).
 * @param {Array<{id: string, name: string}>} items
 * @param {Record<string, string[]>} values
 */
export function formatPlan2DocumentationLines(items = [], values = {}) {
  return (Array.isArray(items) ? items : [])
    .map((item) => {
      const name = String(item?.name || "").trim();
      if (!name) return "";
      const raw = values?.[item.id];
      if (isPlan2Excluded(raw)) return "";
      const text = formatPlan2ValueText(raw);
      return text ? `- ${name}: ${text}` : `- ${name}`;
    })
    .filter(Boolean);
}

/**
 * Merge list-mode tests with table-mode categories into one documentation list.
 * @param {Array} allTests tests with id, name, category
 * @param {object} plan2ByCategory
 * @param {Set|Array} disabledTestIds list-mode exclusions
 * @param {Map|object} testResults list-mode results
 */
export function buildTestsForNoteContext({
  allTests = [],
  plan2ByCategory = null,
  disabledTestIds = new Set(),
  testResults = null,
} = {}) {
  const disabled =
    disabledTestIds instanceof Set
      ? disabledTestIds
      : new Set(Array.isArray(disabledTestIds) ? disabledTestIds : []);
  const resultsMap =
    testResults instanceof Map
      ? testResults
      : testResults && typeof testResults === "object"
        ? new Map(Object.entries(testResults))
        : new Map();

  const byCategory = {};
  (Array.isArray(allTests) ? allTests : []).forEach((test) => {
    const cat = test.category || "other";
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push(test);
  });

  const lines = [];
  PLAN2_TEST_CATEGORY_KEYS.forEach((cat) => {
    const tests = byCategory[cat] || [];
    if (!tests.length) return;
    const state = plan2ByCategory?.[cat];
    if (state?.mode === "table") {
      formatPlan2DocumentationLines(tests, state.values).forEach((line) => lines.push(line));
      return;
    }
    tests.forEach((test) => {
      if (disabled.has(test.id)) return;
      const result = resultsMap.get(test.id);
      let resultText = "";
      if (typeof result === "string") resultText = result.trim();
      else if (result?.prose) resultText = String(result.prose).trim();
      else if (result?.status === "Normal") resultText = "Normal";
      else if (result?.status === "Abnormal")
        resultText = String(result.description || "Abnormal").trim();
      lines.push(`- ${test.name}${resultText ? `: Result: ${resultText}` : ""}`);
    });
  });

  // Any unknown categories fall back to list mode.
  Object.keys(byCategory).forEach((cat) => {
    if (PLAN2_TEST_CATEGORY_KEYS.includes(cat)) return;
    (byCategory[cat] || []).forEach((test) => {
      if (disabled.has(test.id)) return;
      const result = resultsMap.get(test.id);
      const resultText =
        typeof result === "string"
          ? result.trim()
          : result?.prose
            ? String(result.prose).trim()
            : "";
      lines.push(`- ${test.name}${resultText ? `: Result: ${resultText}` : ""}`);
    });
  });

  return lines;
}

/**
 * Merge list-mode treatments with table-mode groups into documentation lines.
 */
export function buildTreatmentsForNoteContext({
  allTreatments = [],
  treatment2ByCategory = null,
  selectedTreatmentIds = null,
  disabledTreatmentIds = new Set(),
  namesOnly = true,
} = {}) {
  const disabled =
    disabledTreatmentIds instanceof Set
      ? disabledTreatmentIds
      : new Set(Array.isArray(disabledTreatmentIds) ? disabledTreatmentIds : []);
  const selected =
    selectedTreatmentIds instanceof Set
      ? selectedTreatmentIds
      : selectedTreatmentIds
        ? new Set(selectedTreatmentIds)
        : null;

  const byGroup = {
    medication: [],
    therapy: [],
    treatmentsBundle: [],
  };
  (Array.isArray(allTreatments) ? allTreatments : []).forEach((treatment) => {
    const group = treatment2GroupKey(treatment.category);
    byGroup[group].push(treatment);
  });

  const lines = [];
  const pushListItem = (treatment) => {
    if (disabled.has(treatment.id)) return;
    if (selected && !selected.has(treatment.id)) return;
    lines.push(`- ${treatment.name}`);
  };

  TREATMENT2_GROUP_KEYS.forEach((group) => {
    const items = byGroup[group] || [];
    if (!items.length) return;
    const state = treatment2ByCategory?.[group];
    if (state?.mode === "table") {
      const values = state.values || {};
      const tableItems = [];
      const listFallback = [];
      items.forEach((treatment) => {
        if (Object.prototype.hasOwnProperty.call(values, treatment.id)) {
          tableItems.push(treatment);
        } else {
          // Custom / late-added rows stay on list keep/hide semantics.
          listFallback.push(treatment);
        }
      });
      formatPlan2DocumentationLines(tableItems, values).forEach((line) => {
        // Table mode includes status text for documentation clarity.
        void namesOnly;
        lines.push(line);
      });
      listFallback.forEach(pushListItem);
      return;
    }
    items.forEach(pushListItem);
  });

  return lines;
}
